import React from 'react';
import ReactPlayer from 'react-player';
import TalkingPoint from '../components/TalkingPoint';
import {Container, styled, useTheme, useMediaQuery, Box, Grid} from '@mui/material';
import backgroundHomeSm from '../assets/background-home-sm.png';
import backgroundHomeLg from '../assets/background-home-lg.png';
import useGetTalkingPoints from '../hooks/useGetTalkingPoints';
import CurrentTalkingPoint from '../components/CurrentTalkingPoint';
import TalkingPointsStack from '../components/TalkingPointsStack';
import {useBoundingclientrectRef, useOnWindowResize} from 'rooks';
import {useVoting} from '../providers/voting';

const ResponsiveReactPlayer = styled(ReactPlayer)({
  position: 'absolute',
  top: 0,
  left: 0,
  border: '1px solid #0000001F',
  background: 'rgba(255, 255, 255, 0.3)',
  backdropFilter: 'blur(5px)',
  borderRadius: '12px',
  overflow: 'hidden',
});

const HomeContainer = styled('div')(({theme}) => ({
  backgroundImage: `url(${backgroundHomeSm})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundAttachment: 'fixed',
  [theme.breakpoints.up('md')]: {
    backgroundImage: `url(${backgroundHomeLg})`,
  },
}));

const VideoContainer = styled('div')(({theme}) => ({
  position: 'fixed',
  top: 0,
  left: 8,
  right: 8,
  zIndex: 1,
  paddingTop: '60%',
  '& video': {
    objectFit: 'cover',
  },
  [`${theme.breakpoints.down('md')} and (min-aspect-ratio: 1/1)`]: {
    position: 'relative',
    left: 'unset',
    right: 'unset',
    top: 'unset',
    height: '400px',
    paddingTop: 0,
  },
  [theme.breakpoints.up('md')]: {
    position: 'relative',
    left: 'unset',
    right: 'unset',
    top: 'unset',
    height: '400px',
    paddingTop: 0,
  },
}));

function Home() {
  const getTalkingPointsResult = useGetTalkingPoints();
  const {currentTalkingPointId} = useVoting();
  const theme = useTheme();
  const isBigScreen = useMediaQuery(theme.breakpoints.up('md'));
  const [myRef, boundingClientRect, updateRect] = useBoundingclientrectRef();

  const currentTalkingPoint = getTalkingPointsResult?.data?.find(
    talkingPoint => talkingPoint.id === currentTalkingPointId
  );

  useOnWindowResize(() => {
    updateRect();
  });

  const skeleton =
    !getTalkingPointsResult?.data?.length &&
    (getTalkingPointsResult?.isValidating || !!getTalkingPointsResult?.error);

  return (
    <HomeContainer>
      <Container
        disableGutters
        sx={{
          px: {xs: '8px', md: 2},
        }}
      >
        <Grid
          container
          spacing={{
            sx: '0',
            md: 2,
          }}
        >
          <Grid item md={7} sx={{zIndex: {md: 1201}, position: 'relative', width: '100%'}}>
            <VideoContainer sx={{mt: {md: 4}}}>
              <ResponsiveReactPlayer
                width="100%"
                height="100%"
                muted
                loop
                // playing
                controls
                url="/video-1.mp4"
              />
            </VideoContainer>

            {isBigScreen && (
              <Box sx={{mt: {md: 2}}}>
                <CurrentTalkingPoint
                  data={currentTalkingPoint}
                  PopupProps={{
                    hideBackdrop: !isBigScreen,
                    paperCoords: {
                      left: boundingClientRect?.left || 0,
                      width: boundingClientRect?.width || 200,
                    },
                  }}
                  skeleton={skeleton || !currentTalkingPoint}
                />
              </Box>
            )}
          </Grid>
          <Grid sx={{width: '100%', zIndex: 0}} item md={5}>
            {!skeleton && (
              <TalkingPointsStack ref={myRef} spacing={7}>
                {getTalkingPointsResult?.data?.map(talkingPoint => (
                  <TalkingPoint
                    key={talkingPoint.id}
                    data={talkingPoint}
                    active={talkingPoint.id === currentTalkingPointId}
                    PopupProps={{
                      hideBackdrop: !isBigScreen,
                      paperCoords: {
                        left: boundingClientRect?.left || 0,
                        width: boundingClientRect?.width || 200,
                      },
                    }}
                  />
                ))}
              </TalkingPointsStack>
            )}
            {skeleton && (
              <TalkingPointsStack spacing={7}>
                <TalkingPoint skeleton />
                <TalkingPoint skeleton />
                <TalkingPoint skeleton />
              </TalkingPointsStack>
            )}
          </Grid>
        </Grid>
      </Container>
    </HomeContainer>
  );
}

export default Home;
